<template>
  <div>
    <loading-overlay v-if="updatingEvents" :text="''" />
    <v-container fluid class="px-6 py-6">
      <!-- Permission Check: Nur für Admins und Manager -->
      <permission-denied 
        v-if="!hasGeneralAdminPermission" 
        message="Du benötigst Manager- oder Administrator-Rechte, um Events zu verwalten."
      />

      <!-- Events Content nur für berechtigte User -->
      <div v-else>
      <!-- Year Selection - Sticky under navbar -->
      <year-selector :sticky="true" />
      <v-row>
        <v-col cols="12">
          <v-row class="mt-0 ml-1" justify="space-between">
            <v-btn
              elevation="0"
              height="43"
              class="font-weight-bold text-uppercase btn-default bg-gradient-primary py-2 px-6 me-2 mb-sm-0 mb-2"
              color="#5e72e4"
              small
              :disabled="!hasAdminOrManagerPermission"
              @click="$router.push('/admin/events/new').catch(() => {})"
              >Neues Event anlegen</v-btn
            >
            <div class="mr-2 d-flex align-center">
              <v-switch
                v-model="showArchived"
                label="Archivierte anzeigen"
                class="mr-4 mt-0"
                hide-details
                @change="loadEvents"
              ></v-switch>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    @click="loadEvents"
                    v-bind="attrs"
                    v-on="on"
                    class="material-icons-round text-primary ms-auto"
                    size="30"
                  >
                    refresh
                  </v-icon>
                </template>
                <span>Aktualisieren</span>
              </v-tooltip>
            </div>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <!-- Keine Events Meldung -->
          <v-row v-if="events.length === 0 && !updatingEvents" class="mb-6 mt-10">
            <v-col cols="12">
              <v-card class="card-shadow border-radius-xl py-10 text-center">
                <div class="mx-auto mb-4">
                  <v-avatar
                    color="bg-gradient-secondary"
                    size="80"
                    class="shadow"
                  >
                    <i class="material-icons-round text-white" style="font-size: 40px;">event_busy</i>
                  </v-avatar>
                </div>
                <h4 class="text-h5 text-typo font-weight-bolder mb-3">
                  Keine Events gefunden
                </h4>
                <p class="text-body font-weight-light mb-4 px-6">
                  Für das Jahr {{ selectedYear || currentYear }} sind keine Events vorhanden.
                  <span v-if="!showArchived"><br>Archivierte Events sind ausgeblendet.</span>
                </p>
              </v-card>
            </v-col>
          </v-row>

          <!-- Events Grid -->
          <v-row class="mb-6 mt-10" v-else>
            <v-col
              v-for="(item, i) in events"
              :key="item.title + i"
              lg="4"
              class="pt-0 mb-10"
            >
              <v-card
                class="card card-shadow border-radius-xl py-5 text-center"
                data-animation="true"
              >
                <div
                  class="mt-n11 mx-4 card-header position-relative z-index-2"
                >
                  <div class="d-block blur-shadow-image" style="position: relative;">
                    <img
                      v-if="item.img && !item.imageError"
                      :src="getImage(item.img)"
                      class="img-fluid shadow border-radius-lg"
                      style="height: 250px; object-fit: cover; width: 100%;"
                      :alt="item.image"
                      @error="handleImageError(item)"
                    />
                    <div
                      v-else
                      class="d-flex align-items-center justify-content-center border-radius-lg shadow"
                      style="
                        height: 250px;
                        width: 100%;
                        background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
                      "
                    >
                      <i class="material-icons-round text-white" style="font-size: 80px; opacity: 0.5;">
                        image_not_supported
                      </i>
                    </div>
                    <!-- Event Date Badge (left) -->
                    <div
                      v-if="item.date"
                      class="position-absolute bottom-0 start-0 ma-3 px-3 py-2 border-radius-md"
                      style="background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(10px);"
                    >
                      <div class="d-flex align-center">
                        <i class="material-icons-round text-white me-1" style="font-size: 18px;">event</i>
                        <span class="text-white text-sm font-weight-bold">
                          {{ formatEventDate(item.date) }}
                        </span>
                      </div>
                    </div>
                    <!-- Event Year Badge (right, only if not current year) -->
                    <div
                      v-if="item.date && !isCurrentYear(item.date)"
                      class="position-absolute bottom-0 end-0 ma-3 px-3 py-2 border-radius-md"
                      style="background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(10px);"
                    >
                      <div class="d-flex align-center">
                        <span class="text-white text-sm font-weight-bold">
                          {{ getEventYear(item.date) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="item.img && !item.imageError"
                    class="colored-shadow"
                    v-bind:style="{
                      backgroundImage: 'url(' + getImage(item.img) + ')',
                    }"
                  ></div>
                </div>
                <div class="d-flex mx-auto mt-n8">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        @click="
                          $router
                            .push('/admin/events/details/' + item._id)
                            .catch(() => {})
                        "
                        class="material-icons-round text-primary ms-auto px-5"
                        size="18"
                      >
                        info
                      </v-icon>
                    </template>
                    <span>Details</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        @click="hasAdminOrManagerPermission && $router.push('/admin/events/edit/' + item._id).catch(() => {})"
                        v-bind="attrs"
                        v-on="hasAdminOrManagerPermission ? on : {}"
                        :class="hasAdminOrManagerPermission ? 'material-icons-round text-info me-auto px-5' : 'material-icons-round text-muted me-auto px-5'"
                        :style="hasAdminOrManagerPermission ? 'cursor: pointer;' : 'cursor: not-allowed; opacity: 0.5;'"
                        size="18"
                      >
                        edit
                      </v-icon>
                    </template>
                    <span>{{ hasAdminOrManagerPermission ? 'Bearbeiten' : 'Bearbeiten nicht verfügbar' }}</span>
                  </v-tooltip>
                </div>
                <h5 class="font-weight-normal text-typo text-h5 mt-7 mb-2 px-4">
                  <a
                    href="javascript:;"
                    class="text-decoration-none text-default"
                    >{{ item.title }}</a
                  >
                </h5>
                <!-- <p class="mb-0 text-body font-weight-light px-5">
                  {{ item.text }}
                </p> -->
                <!-- <span
                  v-if="!item.approved"
                  class="text-xs text-start text-danger"
                  >Die Freigabe dieses Events, durch einen Admin, steht noch
                  aus</span
                > -->
                <hr class="horizontal dark mb-6" />
                <div class="d-flex text-body mx-2 px-4">
                  <p class="mb-0 font-weight-normal text-body">Anmeldungen:</p>
                  <i
                    class="material-icons-round position-relative ms-auto text-lg me-1 my-auto"
                    >person</i
                  >
                  <p class="text-sm my-auto font-weight-light">
                    {{ item.entries.length }}
                  </p>
                </div>
                <!-- Archiviert Badge -->
                <div v-if="item.archived" class="mx-2 px-4 mt-4">
                  <v-alert
                    type="warning"
                    dense
                    text
                    class="mb-0"
                  >
                    <div class="d-flex align-center">
                      <i class="material-icons-round me-2 text-typo" style="font-size: 20px;">archive</i>
                      <div>
                        <p class="mb-0 font-weight-600 text-typo">Event archiviert</p>
                        <p class="mb-0 text-caption text-typo">
                          Archiviert am {{ formatDate(item.archivedAt) }}
                        </p>
                      </div>
                    </div>
                  </v-alert>
                </div>

                <!-- Active Switches (nur wenn nicht archiviert) -->
                <template v-else>
                  <div class="d-flex mb-4 mx-2 px-4 mt-4">
                    <p class="mb-0 text-body font-weight-light">
                      {{ item.active ? "Event aktiv" : "Event inaktiv" }}
                    </p>
                    <v-switch
                      :disabled="!hasAdminOrManagerPermission || (!item.approved && !item.active) || updatingEvent"
                      :ripple="false"
                      class="d-inline-flex mt-0 pt-0 switch ms-auto"
                      v-model="item.active"
                      hide-details
                      @change="updateEvent(item)"
                    ></v-switch>
                  </div>
                  <div class="d-flex mb-4 mx-2 px-4 mt-4">
                    <p class="mb-0 text-body font-weight-light">
                      {{
                        item.entries_active
                          ? "Anmeldung aktiv"
                          : "Anmeldung inaktiv"
                      }}
                    </p>
                    <v-switch
                      :disabled="!hasAdminOrManagerPermission || (!item.approved && !item.entries_active) || updatingEvent"
                      :ripple="false"
                      class="d-inline-flex mt-0 pt-0 switch ms-auto"
                      v-model="item.entries_active"
                      hide-details
                      @change="updateEvent(item)"
                    ></v-switch>
                  </div>
                </template>
                <div class="d-flex mb-4 mx-2 px-4 mt-4">
                  <p
                    class="mb-0 font-weight-light"
                    :class="!item.approved ? 'text-danger' : 'text-success'"
                  >
                    Eventfreigabe {{ item.approved ? "erteilt" : "offen" }}
                  </p>
                  <v-switch
                    disabled
                    :ripple="false"
                    class="d-inline-flex mt-0 pt-0 switch ms-auto success"
                    v-model="item.approved"
                    hide-details
                  ></v-switch>
                </div>
                <span
                  v-if="!item.approved"
                  class="text-xs text-start text-danger"
                  >Dieses Event muss durch einen Admin freigegeben werden.</span
                >
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      </div><!-- End: Events Content -->

      <!-- Merge-Konflikt Dialog -->
      <event-conflict-dialog
        :open="conflictDialogOpen"
        :conflictData="conflictData"
        :localData="conflictLocalData"
        @close="handleConflictClose"
        @use-server-version="handleConflictUseServer"
        @force-my-version="handleConflictForce"
      />
    </v-container>
  </div>
</template>
<script>
// import CalendarWidget from "./Widgets/CalendarWidget.vue";
import NewProduct from "./Widgets/NewProduct.vue";
import coreData from "@/mixins/coreDataMixin";
import PermissionDenied from "@/components/PermissionDenied.vue";
import permissionMixin from "@/mixins/permissionMixin";
import administrationData from "@/mixins/administrationMixin";
import YearSelector from "@/components/YearSelector.vue";
import EventConflictDialog from "./Widgets/EventConflictDialog.vue";

export default {
  name: "Events",
  mixins: [coreData, permissionMixin, administrationData],
  components: {
    NewProduct,
    PermissionDenied,
    YearSelector,
    EventConflictDialog,
  },
  data: function () {
    return {
      showArchived: false,
      updatingEvent: false,
      conflictDialogOpen: false,
      conflictData: null,
      conflictLocalData: null,
      conflictEventRef: null,
    };
  },
  mounted() {
    // Initialize with current year
    const thisYear = new Date().getFullYear();
    if (!this.selectedYear) {
      this.$store.commit('selectedYear', thisYear);
    }
    
    console.log('📅 [EVENTS MOUNT] Loading with year:', this.$store.getters.selectedYear || thisYear);
    this.loadEvents();
  },
  computed: {},
  watch: {
    selectedYear() {
      // Reload events when year changes
      this.loadEvents();
    },
  },
  computed: {},
  methods: {
    handleImageError(item) {
      this.$set(item, 'imageError', true);
    },
    loadEvents() {
      return this.$store.dispatch("getEventsAdmin", { 
        includeArchived: this.showArchived,
        year: this.selectedYear || this.currentYear
      });
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatEventDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    isCurrentYear(dateString) {
      if (!dateString) return true;
      const date = new Date(dateString);
      const currentYear = new Date().getFullYear();
      return date.getFullYear() === currentYear;
    },
    getEventYear(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.getFullYear().toString();
    },

    async updateEvent(event, forceOverwrite) {
      this.updatingEvent = true;
      try {
        let data = {
          _id: event._id,
          active: event.active,
          entries_active: event.entries_active,
        };
        // Conflict-Check: updated-Timestamp mitsenden (außer bei Force-Overwrite)
        if (!forceOverwrite && event.updated) {
          data._lastKnownUpdate = event.updated;
        }
        await this.$store.dispatch("updateEvent", data);
        await this.loadEvents();
      } catch (error) {
        if (error.message === 'CONFLICT' && error.conflictData) {
          this.conflictData = error.conflictData;
          // Save user's intended switch values before reloading
          this.conflictLocalData = { ...event };
          this.conflictEventRef = event;
          // Reload to reset switches to actual server state
          await this.loadEvents();
          this.conflictDialogOpen = true;
        } else {
          console.error('Error updating event:', error);
        }
      } finally {
        this.updatingEvent = false;
      }
    },
    handleConflictClose() {
      // Switches already reflect server state (reloaded on conflict)
      this.conflictDialogOpen = false;
      this.conflictData = null;
      this.conflictLocalData = null;
      this.conflictEventRef = null;
    },
    async handleConflictUseServer() {
      // Switches already reflect server state (reloaded on conflict)
      this.conflictDialogOpen = false;
      this.conflictData = null;
      this.conflictLocalData = null;
      this.conflictEventRef = null;
    },
    async handleConflictForce() {
      this.conflictDialogOpen = false;
      // Restore user's intended switch values before force-saving
      if (this.conflictEventRef && this.conflictLocalData) {
        this.conflictEventRef.active = this.conflictLocalData.active;
        this.conflictEventRef.entries_active = this.conflictLocalData.entries_active;
      }
      if (this.conflictEventRef) {
        await this.updateEvent(this.conflictEventRef, true);
      }
      this.conflictData = null;
      this.conflictLocalData = null;
      this.conflictEventRef = null;
    },
  },
};
</script>
